import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { MoreHorizontal, ChevronRight, ShoppingBag, Store, UserCheck } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from 'components/ui/dropdown-menu'
import Button from 'components/Button'
import Card from '../Card'
import S from '../Card.style'
import useMediaQuery from 'hooks/useMediaQuery'
import * as _ from 'styles/helpers'

export interface BusinessCardWrapperProps {
	linkUrl?: string
	linkState?: any
	children: any
}

export interface BusinessCardProps {
	className?: string
	name: string
	address1: string
	address2: string
	image: string
	isCenter?: boolean
	linkUrl?: string
	linkState?: any
	editUrl?: string
	menuItems?: {
		title: string
		link?: string
		callback?: Function
		hasDividerAfter?: boolean
		isDanger?: boolean
	}[]
	onThreeDotClick?: () => void
	isHorizontal?: boolean
	isOwner?: boolean
	ownerImage?: string
	ownerName?: string
	ownerCompany?: string
	ownerEmail?: string
}

const BusinessCardWrapper = ({ linkUrl, linkState, children }: BusinessCardWrapperProps) => {
	if (linkUrl) return <Link to={linkUrl} state={linkState}>{children}</Link>
	return children
}

export const BusinessCard = ({
	className = '',
	name,
	address1,
	address2,
	image,
	isCenter,
	editUrl,
	menuItems,
	onThreeDotClick,
	linkUrl,
	linkState,
	isHorizontal = false,
	isOwner,
	ownerImage,
	ownerName,
	ownerCompany,
	ownerEmail,
}: BusinessCardProps) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const [isModalOpen, setIsModalOpen] = useState(false)

	const handleAvatarClick = () => {
		setIsModalOpen(true)
	}

	const handleModalClose = () => {
		setIsModalOpen(false)
	}

	const actions = () => {
		if (editUrl)
			return (
				<Button as={Link} isOutline to={editUrl}>
					Edit
				</Button>
			)
		if (onThreeDotClick) {
			return (
				<>
					{isOwner ? (
						<button
							type="button"
							title="Options"
							className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
							onClick={onThreeDotClick}
						>
							<MoreHorizontal className="w-5 h-5" />
						</button>
					) : (
						<Avatar className="cursor-pointer" onClick={handleAvatarClick}>
							<AvatarImage src={ownerImage} />
							<AvatarFallback />
						</Avatar>
					)}
				</>
			)
		}
		if (menuItems) {
			return (
				<>
					{isOwner ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									title="Options"
									className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
								>
									<MoreHorizontal className="w-5 h-5" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[15ch]">
								{menuItems.map((menuItem, index) => (
									<React.Fragment key={index}>
										<DropdownMenuItem
											className={menuItem.isDanger ? 'text-red-500 focus:text-red-500' : ''}
											onClick={() => menuItem.callback?.()}
										>
											{menuItem.link ? (
												<Link to={menuItem.link} className="w-full">
													{menuItem.title}
												</Link>
											) : (
												menuItem.title
											)}
										</DropdownMenuItem>
										{menuItem.hasDividerAfter && <DropdownMenuSeparator />}
									</React.Fragment>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<Avatar className="cursor-pointer" onClick={handleAvatarClick}>
							<AvatarImage src={ownerImage} />
							<AvatarFallback />
						</Avatar>
					)}
				</>
			)
		}
	}

	const ownerInfoModal = (
		<Dialog open={isModalOpen} onClose={handleModalClose}>
			<DialogContent className="max-w-[350px] p-8 relative">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<UserCheck className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Contact Info</DialogTitle>
							<DialogDescription>View contact details</DialogDescription>
						</div>
					</div>
				</DialogHeader>
				<div className="flex justify-center mt-2">
					<Avatar className="h-[100px] w-[100px]">
						<AvatarImage src={ownerImage} />
						<AvatarFallback />
					</Avatar>
				</div>
				<h2 className="pt-4 text-xl font-medium text-center">{ownerName}</h2>
				<h2 className="text-xl font-medium text-center">{ownerCompany}</h2>
				<p className="text-sm text-center">
					<a
						href={`mailto:${ownerEmail}?subject=${encodeURIComponent(name)}`}
						className="text-blue-500 hover:underline"
					>
						{ownerEmail}
					</a>
				</p>
			</DialogContent>
		</Dialog>
	)

	if (isMobile) {
		return (
			<>
				<div data-testid="BusinessCard" className={`bg-white rounded-lg shadow-sm overflow-hidden mb-3 ${className}`}>
					<Link
						to={linkUrl || '#'}
						state={linkState}
						className="flex items-center px-4 py-3 no-underline text-inherit transition-colors hover:bg-gray-100 active:bg-gray-200"
					>
						<Avatar className="shrink-0 mr-3 h-14 w-14">
							<AvatarImage src={image} />
							<AvatarFallback>
								{isCenter ? <ShoppingBag className="w-6 h-6" /> : <Store className="w-6 h-6" />}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 min-w-0">
							<h3 className="m-0 mb-1 text-base font-semibold text-black truncate">
								{name.length > 28 ? `${name.slice(0, 25)}...` : name}
							</h3>
							<p className="m-0 text-[13px] leading-snug text-gray-600">
								{address1}
								<br />
								{address2}
							</p>
						</div>
						<ChevronRight className="shrink-0 w-6 h-6 text-gray-400" />
					</Link>
				</div>
				{ownerInfoModal}
			</>
		)
	}

	return (
		<>
			<Card actions={actions()} isCenteredText>
				<div data-testid="BusinessCard" className={`w-full [&_a:hover]:no-underline ${className}`}>
					<BusinessCardWrapper linkUrl={linkUrl} linkState={linkState}>
						<div className={isHorizontal ? 'flex' : ''}>
							<div
								className={
									isHorizontal ? 'flex-[250px_0_0] flex flex-col items-center justify-center -m-4 mr-0 p-4' : ''
								}
							>
								<S.Title>{name.length > 22 ? `${name.slice(0, 17)}. . .` : name}</S.Title>{' '}
								<S.Description>
									{address1}
									<br />
									{address2}
								</S.Description>
								{isCenter ? (
									<ShoppingBag className="w-6 h-6 mx-auto mt-1" />
								) : (
									<Store className="w-6 h-6 mx-auto mt-1" />
								)}
							</div>
							<div>
								<S.Image
									className={isHorizontal ? '-m-4 ml-0 pt-[25%] flex-1' : ''}
									style={!isHorizontal ? { margin: '1rem -1rem -1rem -1rem' } : undefined}
									src={image}
								/>
							</div>
						</div>
					</BusinessCardWrapper>
				</div>
			</Card>
			{ownerInfoModal}
		</>
	)
}

export default BusinessCard
